import { AbstractControl, AbstractControlOptions, AsyncValidatorFn, FormArray, FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { GuardErrorMessage, INotificationService, IoCContainer, ITranslation, IUtils, IWebCoreIoCContainer, PropertyErrorMessage, ServerSideValidationError, WebCoreIoCKeys, WecdevError, WecdevErrorMessage, WecdevErrorMessageType, WecdevErrorType } from '@lmnop/web-core';
import { AngularCoreIoCKeys, IAngularCoreIoCContainer } from 'angular-core-src';

export class ExtendedFormGroup<FormValueType> extends FormGroup {
    constructor(
        htmlId: string,
        formValueType: { new (): FormValueType },
        controls: {
            [key: string]: AbstractControl;
        },
        validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
        asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
    ) {
        super(controls, validatorOrOpts);

        this.formValueType = formValueType;
        this.ioc = IoCContainer.get(AngularCoreIoCKeys.IAngularCoreIoCContainer);

        let originalValue = this.getValue();
        this.snapshotOriginalValueAndPatchFormValue(originalValue);
    }

    protected ioc: IAngularCoreIoCContainer;

    private formValueType: any;
    private originalValue: FormValueType;

    private getHandlerValidationErrorPromise<T>(): Promise<T> {
        return Promise.reject<T>(new WecdevError(WecdevErrorType.VALIDATION_ERROR, WecdevError.ERROR_HANDLED));
    }

    private getErrorMessage(errorMessage): string {
        let webCoreIoC: IWebCoreIoCContainer = IoCContainer.get(WebCoreIoCKeys.IWebCoreIoCContainer);
        let translation: ITranslation = webCoreIoC.translation;

        return translation.getMessage('error_messages.' + errorMessage);
    }

    public handleServerSideValidationError(error: WecdevError): Promise<WecdevError> {
        let notificationService: INotificationService = IoCContainer.get(WebCoreIoCKeys.INotificationService);
        let utils: IUtils = IoCContainer.get(WebCoreIoCKeys.IUtils);
        if (error.type === WecdevErrorType.VALIDATION_ERROR) {
            error.errors.forEach((errorMessage: WecdevErrorMessage): void => {
                if (errorMessage.type === WecdevErrorMessageType.GUARD_ERROR) {
                    let guardErrorMessage: GuardErrorMessage = <GuardErrorMessage>errorMessage;
                    notificationService.createWarningTranslateMessage(this.getErrorMessage(guardErrorMessage.message));
                } else if (errorMessage.type === WecdevErrorMessageType.PROPERTY_ERROR) {
                    let propertyErrorMessage: PropertyErrorMessage = <PropertyErrorMessage>errorMessage;
                    let control: AbstractControl = this.get(propertyErrorMessage.propertyName.decapitalizeFirstLetter());
                    if (utils.isJsonString(propertyErrorMessage.message)) {
                        let serverSideValidationError: ServerSideValidationError = JSON.parse(propertyErrorMessage.message);
                        control.setErrors({
                            [serverSideValidationError.serverSideValidatorName]: serverSideValidationError.params,
                        });
                    } else {
                        //we get a string to translate
                        control.setErrors({
                            [propertyErrorMessage.message]: true,
                        });
                    }
                } else {
                    throw new Error('Invalid errorMessage.type: ' + errorMessage.type);
                }
            });
            return this.getHandlerValidationErrorPromise();
        }
        return Promise.reject<WecdevError>(error);
    }

    public showFieldError(formControlName: string): boolean {
        let res: boolean = this.get(formControlName).invalid && this.get(formControlName).touched;
        return res;
    }

    public getErrorMessageHtml(formControlName: string): string {
        let control: AbstractControl = this.get(formControlName);
        let res: string[] = [];
        if (control.errors) {
            Object.keys(control.errors).forEach((errorName: string): void => {
                let errorMessage: string = this.getErrorMessage(errorName);
                if (!errorMessage) {
                    throw new Error('Missing errormessage: ' + errorName);
                }
                //there are parameters
                if (typeof control.errors[errorName] === 'object') {
                    Object.keys(control.errors[errorName]).forEach((parameterName: string): void => {
                        errorMessage = errorMessage.replaceAll(`{{${parameterName}}}`, control.errors[errorName][parameterName]);
                    });
                }
                res.push(`<label>${errorMessage}</label>`);
            });
            return res.join('<br/>');
        }
        return '';
    }

    public validateAllFormFields(): boolean {
        return this.validateAllFormFieldsRecursive(this);
    }

    private validateAllFormFieldsRecursive(formGroup: FormGroup): boolean {
        //we consider the disabled form as valid form
        if (this.disabled) {
            return true;
        }
        if (this.invalid) {
            Object.keys(formGroup.controls).forEach((name: string): void => {
                const control: AbstractControl = formGroup.get(name);
                if (control instanceof FormControl) {
                    control.markAsTouched({ onlySelf: true });
                } else if (control instanceof FormGroup) {
                    this.validateAllFormFieldsRecursive(control);
                } else if (control instanceof FormArray) {
                    control.controls.forEach((arrayFormGroup: FormGroup): void => {
                        this.validateAllFormFieldsRecursive(arrayFormGroup);
                    });
                }
            });
            //set focus to the first invalid control
            //todo spoiler protecionnél nem működik ez, mert nincs rajta ng invalid
            //(<HTMLElement>document.querySelectorAll('.floating-label .ng-invalid')[0]).focus();
        }
        return this.valid;
    }

    /**
     * @deprecated
     * Not supported, please reinit the formgroup
     */
    public override reset(): void {
        super.reset();
    }

    /**
     * @deprecated
     * Not supported, please use getValue() or getOriginalClonedValue()
     */
    public override getRawValue(): any {
        return super.getRawValue();
    }

    /**
     * @deprecated
     * Not supported, please use setValue()
     */
    public override patchValue(formValue: any): any {
        return super.patchValue(formValue);
    }

    public override setValue(formValue: FormValueType): void {
        this.snapshotOriginalValueAndPatchFormValue(formValue);
    }

    public setControlValue(controlName: string, controlValue: any): void {
        let formValue: any = this.getValue();
        formValue[controlName] = controlValue;
        super.patchValue(formValue);
    }

    public getControlValue(controlName: string): any {
        let formValue: any = this.getValue();
        return formValue[controlName];
    }

    private snapshotOriginalValueAndPatchFormValue(formValue: FormValueType) {
        super.patchValue(formValue);
        this.originalValue = new this.formValueType();
        this.ioc.core.utils.deepCopy(this.originalValue, super.getRawValue());
    }

    public getValue(trimString: boolean = true): FormValueType {
        let formValue = new this.formValueType();
        this.ioc.core.utils.deepCopy(formValue, super.getRawValue(), trimString);
        return formValue;
    }

    public getOriginalClonedValue(): FormValueType {
        return this.originalValue;
    }
}
